/**
 * WebSocket service for real-time messaging
 * Manages WebSocket connection with auto-reconnect and event handling
 */

import type {
  WebSocketEvent,
  OutgoingWebSocketEvent,
  NewMessageEvent,
  MessageReadEvent,
  UserTypingEvent,
  UserStoppedTypingEvent,
  ConnectedEvent,
  ErrorEvent,
} from '../types/messaging';

export type WebSocketEventHandler = (event: WebSocketEvent) => void;

export interface WebSocketServiceOptions {
  url?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  pingInterval?: number;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private token: string | null = null;
  private reconnectInterval: number;
  private maxReconnectAttempts: number;
  private pingInterval: number;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private isIntentionallyClosed = false;
  private eventHandlers: Map<string, Set<WebSocketEventHandler>> = new Map();

  constructor(options: WebSocketServiceOptions = {}) {
    const baseUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8080';
    this.url = options.url || `${baseUrl}/ws/messages`;
    this.reconnectInterval = options.reconnectInterval || 3000;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10;
    this.pingInterval = options.pingInterval || 30000;
  }

  /**
   * Connect to WebSocket server with authentication token
   */
  connect(token: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.warn('WebSocket already connected');
      return;
    }

    this.token = token;
    this.isIntentionallyClosed = false;
    this.reconnectAttempts = 0;

    this.createConnection();
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.cleanup();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Send an event to the WebSocket server
   */
  send(event: OutgoingWebSocketEvent): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, cannot send message');
      return;
    }

    try {
      this.ws.send(JSON.stringify(event));
    } catch (error) {
      console.error('Failed to send WebSocket message:', error);
    }
  }

  /**
   * Register an event handler for a specific event type
   */
  on(eventType: string, handler: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set());
    }
    this.eventHandlers.get(eventType)!.add(handler);
  }

  /**
   * Unregister an event handler
   */
  off(eventType: string, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.eventHandlers.delete(eventType);
      }
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Get current connection state
   */
  getState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  /**
   * Create WebSocket connection
   */
  private createConnection(): void {
    if (!this.token) {
      console.error('Cannot connect: no authentication token');
      return;
    }

    try {
      const wsUrl = `${this.url}?token=${encodeURIComponent(this.token)}`;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket open event
   */
  private handleOpen(): void {
    console.log('WebSocket connected');
    this.reconnectAttempts = 0;

    // Start ping/pong heartbeat
    this.startPing();

    // Emit connection event
    this.emit({
      type: 'connected',
      timestamp: new Date().toISOString(),
    } as ConnectedEvent);
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data) as WebSocketEvent;
      this.emit(data);
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  /**
   * Handle WebSocket error event
   */
  private handleError(event: Event): void {
    console.error('WebSocket error:', event);

    this.emit({
      type: 'error',
      message: 'WebSocket connection error',
    } as ErrorEvent);
  }

  /**
   * Handle WebSocket close event
   */
  private handleClose(event: CloseEvent): void {
    console.log('WebSocket closed:', event.code, event.reason);

    this.cleanup();

    // Attempt reconnection if not intentionally closed
    if (!this.isIntentionallyClosed) {
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.emit({
        type: 'error',
        message: 'Failed to reconnect to server',
      } as ErrorEvent);
      return;
    }

    this.reconnectAttempts++;
    console.log(
      `Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${this.reconnectInterval}ms`
    );

    this.reconnectTimer = setTimeout(() => {
      this.createConnection();
    }, this.reconnectInterval);
  }

  /**
   * Start ping/pong heartbeat
   */
  private startPing(): void {
    this.pingTimer = setInterval(() => {
      if (this.isConnected()) {
        this.send({ type: 'ping' });
      }
    }, this.pingInterval);
  }

  /**
   * Cleanup timers and resources
   */
  private cleanup(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  /**
   * Emit event to registered handlers
   */
  private emit(event: WebSocketEvent): void {
    // Call handlers for specific event type
    const handlers = this.eventHandlers.get(event.type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          console.error(`Error in event handler for ${event.type}:`, error);
        }
      });
    }

    // Call handlers for all events
    const allHandlers = this.eventHandlers.get('*');
    if (allHandlers) {
      allHandlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          console.error('Error in wildcard event handler:', error);
        }
      });
    }
  }
}

// Singleton instance
let websocketInstance: WebSocketService | null = null;

/**
 * Get or create WebSocket service singleton
 */
export function getWebSocketService(options?: WebSocketServiceOptions): WebSocketService {
  if (!websocketInstance) {
    websocketInstance = new WebSocketService(options);
  }
  return websocketInstance;
}

/**
 * Reset WebSocket service (for testing)
 */
export function resetWebSocketService(): void {
  if (websocketInstance) {
    websocketInstance.disconnect();
    websocketInstance = null;
  }
}
